import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import * as Popover from "@radix-ui/react-popover";
import {
  ChevronDown,
  MessageSquarePlus,
  Pencil,
  Trash2,
  Check,
  X,
} from "lucide-react";
import type { ThreadInfo } from "../hooks/use-document-chat";

interface ChatThreadHeaderProps {
  threads: ThreadInfo[];
  activeThreadId: string | null;
  onNewThread: () => void;
  onSelectThread: (threadId: string) => void;
  onDeleteThread: (threadId: string) => void;
  onRenameThread: (threadId: string, title: string) => void;
}

function formatRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

function getThreadDisplayName(thread: ThreadInfo): string {
  return thread.threadTitle ?? truncate(thread.firstQuestion, 40);
}

function ThreadRow({
  thread,
  isActive,
  onSelect,
  onDelete,
  onRename,
}: {
  thread: ThreadInfo;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const startEditing = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setEditValue(thread.threadTitle ?? thread.firstQuestion);
      setIsEditing(true);
    },
    [thread.threadTitle, thread.firstQuestion],
  );

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditValue("");
  }, []);

  const confirmRename = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== (thread.threadTitle ?? thread.firstQuestion)) {
      onRename(trimmed);
    }
    setIsEditing(false);
  }, [editValue, thread.threadTitle, thread.firstQuestion, onRename]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        confirmRename();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelEditing();
      }
    },
    [confirmRename, cancelEditing],
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete();
    },
    [onDelete],
  );

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={confirmRename}
          className="flex-1 min-w-0 text-sm bg-surface-secondary rounded px-1.5 py-0.5 outline-none ring-1 ring-border focus:ring-accent"
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={confirmRename}
          className="p-0.5 text-accent hover:text-accent/80 cursor-pointer"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancelEditing}
          className="p-0.5 text-muted hover:text-primary cursor-pointer"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      role="option"
      aria-selected={isActive}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSelect();
      }}
      className="group/thread w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-surface-secondary transition-colors text-left cursor-pointer"
    >
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span
          className={`text-sm truncate ${isActive ? "font-medium text-primary" : "text-secondary"}`}
        >
          {getThreadDisplayName(thread)}
        </span>
        <span className="text-[11px] text-muted">
          {thread.questionCount}{" "}
          {thread.questionCount === 1 ? "message" : "messages"} ·{" "}
          {formatRelativeTime(thread.lastMessageAt)}
        </span>
      </div>

      <div className="flex items-center gap-0.5 opacity-0 group-hover/thread:opacity-100 transition-opacity shrink-0">
        <button
          type="button"
          onClick={startEditing}
          className="p-1 rounded hover:bg-surface-tertiary text-muted hover:text-primary transition-colors cursor-pointer"
        >
          <Pencil className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className="p-1 rounded hover:bg-destructive/10 text-muted hover:text-destructive transition-colors cursor-pointer"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export function ChatThreadHeader({
  threads,
  activeThreadId,
  onNewThread,
  onSelectThread,
  onDeleteThread,
  onRenameThread,
}: ChatThreadHeaderProps) {
  const [open, setOpen] = useState(false);
  const activeThread = threads.find((t) => t.threadId === activeThreadId);

  const handleSelect = useCallback(
    (threadId: string) => {
      onSelectThread(threadId);
      setOpen(false);
    },
    [onSelectThread],
  );

  const triggerLabel = activeThread
    ? truncate(getThreadDisplayName(activeThread), 30)
    : "Select conversation";

  return (
    <div className="flex items-center mt-2 justify-between px-4 ml-4 py-2 border-b mr-2 border-border/60">
      <button
        type="button"
        onClick={onNewThread}
        className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 transition-colors cursor-pointer"
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        New Chat
      </button>

      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs text-secondary hover:text-primary transition-colors cursor-pointer max-w-[200px]"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronDown className="h-3 w-3 shrink-0" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={6}
            className="z-50 w-72 rounded-lg border border-border bg-surface shadow-lg p-1.5 animate-in fade-in-0 zoom-in-95"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="px-2 py-1.5 text-xs font-medium text-muted">
              Conversations
            </div>

            <div className="max-h-64 overflow-y-auto">
              {threads.map((thread) => (
                <ThreadRow
                  key={thread.threadId}
                  thread={thread}
                  isActive={thread.threadId === activeThreadId}
                  onSelect={() => handleSelect(thread.threadId)}
                  onDelete={() => onDeleteThread(thread.threadId)}
                  onRename={(title) => onRenameThread(thread.threadId, title)}
                />
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
